import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Activity, ArrowLeft, Calendar, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { blogAPI } from '../utils/api';

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await blogAPI.getPosts(true); // Only published
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          >
            <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
              <Activity className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold font-heading text-slate-900">www.vtoroemnenie.com</span>
          </button>
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            На главную
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold font-heading text-slate-900 mb-4">Блог</h1>
          <p className="text-xl text-slate-600">
            Статьи и новости о радиологии и медицинской диагностике
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-600">Загрузка статей...</div>
        ) : posts.length === 0 ? (
          <Card className="p-12 bg-white rounded-xl border border-slate-100 text-center">
            <p className="text-slate-600 mb-4">Статьи скоро появятся</p>
            <Button
              onClick={() => navigate('/')}
              className="bg-teal-600 text-white hover:bg-teal-700 rounded-lg"
            >
              Вернуться на главную
            </Button>
          </Card>
        ) : (
          <div className="space-y-8">
            {posts.map((post) => (
              <Card
                key={post.id}
                className="p-8 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-lg transition-all cursor-pointer"
                onClick={() => navigate(`/blog/${post.id}`)}
              >
                <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {new Date(post.created_at).toLocaleDateString('ru-RU', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                  <div className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {post.author}
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3 hover:text-teal-600 transition-colors">
                  {post.title}
                </h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  {post.excerpt}
                </p>
                <Button
                  variant="outline"
                  className="rounded-lg"
                >
                  Читать далее →
                </Button>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function BlogPostPage() {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPost();
  }, [postId]);

  const fetchPost = async () => {
    try {
      const response = await blogAPI.getPost(postId);
      setPost(response.data);
    } catch (error) {
      console.error('Failed to load blog post');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-600">Загрузка статьи...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="p-12 bg-white rounded-xl text-center max-w-md">
          <p className="text-slate-600 mb-4">Статья не найдена</p>
          <Button
            onClick={() => navigate('/blog')}
            className="bg-teal-600 text-white hover:bg-teal-700 rounded-lg"
          >
            Вернуться к блогу
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          >
            <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
              <Activity className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold font-heading text-slate-900">www.vtoroemnenie.com</span>
          </button>
          <Button
            onClick={() => navigate('/blog')}
            variant="ghost"
            className="text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            К блогу
          </Button>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="mb-8">
          <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {new Date(post.created_at).toLocaleDateString('ru-RU', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>
            <div className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {post.author}
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold font-heading text-slate-900 mb-6 leading-tight">
            {post.title}
          </h1>
          <p className="text-xl text-slate-600 italic">
            {post.excerpt}
          </p>
        </div>

        <Card className="p-8 bg-white rounded-xl border border-slate-100 shadow-sm">
          <div className="prose prose-lg max-w-none">
            <div className="text-slate-700 leading-relaxed whitespace-pre-wrap">
              {post.content}
            </div>
          </div>
        </Card>

        <div className="mt-8 flex justify-center">
          <Button
            onClick={() => navigate('/blog')}
            variant="outline"
            className="rounded-lg"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Вернуться к блогу
          </Button>
        </div>
      </article>
    </div>
  );
}
